import ControlEngine from '../../ControlEngine/ControlEngine';
import ControlState from '../../Shared/controlState';
import { useFormContext } from 'react-hook-form';
import {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useState
} from 'react';
import { WorkflowEngineContext } from '../../WorkflowEngine/workflowEngineReducer';
import getControlsUtilizeHiddenComponents from './getControlsUtilizeHiddenComponents';
import getDataPropertiesThatUtilizeHiddenComponents from './getDataPropertiesThatUtilizeHiddenComponents';
import evaluteControlsVisibility from './ConditionalControlUtils/evaluateControlsVisibility';
import reEvaluateHiddenControls from './ConditionalControlUtils/reEvaluateHiddenControls';
import getWorkflowFlattenData from '../../Shared/getWorkflowFlattenData';
import JSONSchema7Properties from '../../Shared/jSONSchema7Properties';
import Grid from '@mui/material/Grid';

export interface ConditionalControlsRef {
  triggerValidation: () => Promise<boolean>;
  sanitizeNumericFields: () => Record<string, unknown>;
}

export default forwardRef(
  ({ pageControls }: { pageControls: ControlState[] }, ref) => {
    const { state, dispatch } = useContext(WorkflowEngineContext);
    const [localFormData, setLocalFormData] =
      useState<Record<string, unknown>>();
    const { pages, workflowData } = state;

    const formContext = useFormContext();

    const subscribeToFormChanges = () => {
      const controlsThatUtilizeHiddenComponents =
        getControlsUtilizeHiddenComponents(pages);

      const propertiesControlsThatUtilizeHiddenComponents =
        getDataPropertiesThatUtilizeHiddenComponents(
          controlsThatUtilizeHiddenComponents
        );

      return formContext?.watch((formData, { name }) => {
        setLocalFormData(formData);
        if (
          !propertiesControlsThatUtilizeHiddenComponents.includes(
            name as string
          )
        ) {
          return; // skip properties that not utilize hidden components
        }

        const data = {
          ...getWorkflowFlattenData(workflowData),
          ...formData
        };

        evaluteControlsVisibility(pages, data, dispatch);

        reEvaluateHiddenControls(pages, dispatch);

        // fix signature controls not responding immediately after becoming visible
        setTimeout(() => window.dispatchEvent(new Event('resize')), 0);
      });
    };

    useImperativeHandle(ref, () => ({
      async triggerValidation() {
        return await formContext.trigger();
      },
      sanitizeNumericFields() {
        const localFormDataCopy = localFormData;
        for (const id in localFormDataCopy) {
          const control = pageControls.find((control) => control.id === id);
          if (control?.kind === 'numeric') {
            const value = localFormDataCopy[id];
            localFormDataCopy[id] = value === '' ? NaN : Number(value);
          }
        }
        return localFormDataCopy;
      }
    }));

    useEffect(() => {
      const subscription = subscribeToFormChanges();
      return () => subscription.unsubscribe();
    }, [formContext]);

    useEffect(() => {
      evaluteControlsVisibility(
        pages,
        getWorkflowFlattenData(workflowData),
        dispatch
      );
    }, [workflowData]);
    return (
      <Grid container spacing={3}>
        {pageControls.map((control) => {
          const gridSize = (
            control.schema?.properties as JSONSchema7Properties
          )['gridSize']?.default;
          return (
            <Grid
              item
              xs={gridSize ? (gridSize as number) : 12}
              key={`${control.pageId}-${control.id}`}
              sx={control.visible ? {} : { display: 'none' }}
            >
              <ControlEngine control={control} readOnly />
            </Grid>
          );
        })}
      </Grid>
    );
  }
);
